import { z } from "zod";

export const PlanSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullish(),
  external_id: z.string().nullish(),
  external_source: z.string().nullish(),
  renewal_cycle_credits_amount: z.number(),
  credits_renewal_cycle_days: z.number(),
  carryover_unused_credits: z.boolean().default(false),
  price: z.number().nullish(),
  currency: z.string().default("usd"),
  billing_cycle_days: z.number().nullish(),
  is_active: z.boolean().default(true),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullish(),
});

export const SubscriptionBaseOrAddonSchema = z.object({
  amount: z.number(),
  cancel_at_period_end: z.boolean(),
  change_reason: z.enum(["TRIAL_END_UPDATE", "UPGRADE", "DOWNGRADE"]).nullish(),
  change_to_plan_id: z.string().nullish(),
  end_date: z.number().nullish(),
  is_active_base_subscription: z.boolean(),
  plan_id: z.string(),
  plan_name: z.string(),
  price_id: z.string().nullish(),
  product_id: z.string().nullish(),
  remaining_credits: z.number(),
  start_date: z.number().nullish(),
  stripe_subscription_id: z.string().nullish(),
  subscription_id: z.string(),
  subscription_type: z.enum(["BASE", "ADDON"]),
  total_credits: z.number(),
  trial_end: z.number().nullish(),
  trial_start: z.number().nullish(),
});

export const SubscriptionSchema = z.object({
  user_id: z.string(),
  total_credits_limit: z.number(),
  total_remaining_credits: z.number(),
  addon_subscriptions: z.array(SubscriptionBaseOrAddonSchema),
  base_subscriptions: z.array(SubscriptionBaseOrAddonSchema),
});

export const AddonPaymentAutochargeSchema = z.object({
  success: z.literal(true),
  amount_due: z.number(),
  amount_paid: z.number(),
  currency: z.string(),
  invoice_id: z.string(),
  invoice_pdf: z.string(),
  invoice_url: z.string(),
});

export const AddonPaymentManualChargeSchema = z.object({
  success: z.literal(false),
  error_type: z.string(),
  message: z.string(),
  next_step: z.object({
    message: z.string(),
    type: z.enum(["payment_intent_client_secret", "hosted_invoice_url", "billing_portal_url"]),
    value: z.string(),
  }),
});

export const CreditLogSchema = z.object({
  log_id: z.string(),
  user_id: z.string(),
  operation: z.enum(["CONSUME", "REFUND", "TOPUP", "EXPIRED"]),
  credit: z.number(),
  tool_name: z.string().nullish(),
  created_at: z.string(),
  is_subscription: z.boolean(),
});

export const OrderFulfillmentSchema = z.object({
  amount: z.number(),
  currency: z.string(),
  session_id: z.string(),
  session_status: z.enum(["open", "complete", "expired"]),
  email: z.string(),
  user_id: z.string().nullable(),
});

export type Plan = z.infer<typeof PlanSchema>;
export type CreditLog = z.infer<typeof CreditLogSchema>;
export type Subscription = z.infer<typeof SubscriptionSchema>;
export type SubscriptionBaseOrAddon = z.infer<typeof SubscriptionBaseOrAddonSchema>;
export type AddonPaymentAutocharge = z.infer<typeof AddonPaymentAutochargeSchema>;
export type AddonPaymentManualCharge = z.infer<typeof AddonPaymentManualChargeSchema>;
export type OrderFulfillment = z.infer<typeof OrderFulfillmentSchema>;
